import React from "react";
// import { useSelector } from "react-redux";
import { Confirm } from "rhombus-react";
import DeleteConcept from "widgets/modals/DeleteConcept";
import DeleteAsset from "widgets/modals/assets/DeleteAsset";
import DeleteCreatives from "widgets/modals/creatives/DeleteCreatives";
import DeleteVersions from "widgets/modals/versions/DeleteVersions";
import { loadCreative } from "reducers/CreativeDetails";
import { deleteAssets } from "./Assets";
import createReducer from "./createReducer";
import InfoBanner from "widgets/InfoBanner";
import { labelWithCount } from "utils/common";
import ConfirmPreviews from "widgets/modals/previews/ConfirmPreviews";
import { loadCreativePreviews } from "./CreativePreviews";
import { loadCreatives } from "./Creatives";
import { isAfterEffectsAsset } from "widgets/grid/cells/AssetActions";
import { openModal } from "./Modal";
import { MODAL_TYPES } from "utils/constants";


export function confirmDeleteVersion(onCommitDsp, confirmProps){
	let { creativeId, versionIds } = confirmProps;
	let commitLabel = (versionIds.length === 1) ? "Delete Version" : `Delete Versions (${versionIds.length})`;

	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title: "Delete Versions",
			className: "delete-versions-confirmation",
			desc: <DeleteVersions versionIds={versionIds} />,

			commitLabel,
			commitTheme: "red",
			onCommit: () => {
				return dispatch(onCommitDsp(confirmProps)).then((response) => {
					if(response?.success){
						// Update number of versions in the header, etc.
						dispatch(loadCreative(creativeId));
					}
					return response;
				});
			},
		}));
	};
}

export function confirmPreviews(commitAction, confirmProps, refreshGrid = false) {
	const { title, commitLabel, desc, previews, libraryId, commitTheme } = confirmProps;

	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title,
			className: "confirm-previews",
			desc: <ConfirmPreviews previews={previews} desc={desc} />,
			commitLabel: commitLabel ? commitLabel : title,
			commitTheme,
			onCommit: () => dispatch(commitAction(libraryId, {
				items: previews.map(preview => (
					{
						id: preview.id,
						type: preview.type,
						name: preview.name,
						status: preview.status,
						isPermanent: preview.isPermanent,
					}
				)),
			})).then((resp) => {
				if (!resp.success) {
					return Promise.reject({ success: false });
				}

				if (refreshGrid) {
					dispatch(loadCreativePreviews(libraryId));
				}

				// Close Modal
				return Promise.resolve({ success: true });
			}),
		}));
	};
}

export function confirmDeleteCreatives(deleteAction, confirmProps){
	let { creatives, libraryId } = confirmProps;
	let buttonText = creatives.length > 1 ? "Delete Creatives" : "Delete Creative";
	let titleText = creatives.length > 1 ? `Delete Creative - ${creatives.length} Creatives` : "Delete Creative";

	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title: titleText,
			className: "delete-creative-confirm",
			desc: <DeleteCreatives creatives={creatives} />,
			commitLabel: labelWithCount(buttonText, creatives.length),
			onCommit: () => {
				return dispatch(deleteAction(libraryId, creatives.map(creative => creative.id)))
					.then((resp) => {
						if(resp?.success){
							/*
								It's possible for some of the request to succeed, but some creatives
								won't be deleted because they're assigned to a placement.

								If any of them are assigned, we show a modal to notify the user.
							*/

							const assigned = resp.payload?.data?.assigned || [];
							if(assigned.length){
								/*
									The confirm modal doesn't close until its onCommit returned promise resolves,
									so we refresh the creatives first, before we open the errors modal,
									so we don't have multiple modals open at once, while the request is in flight.
								*/
								return dispatch(loadCreatives(libraryId))
									.then(() => {
										dispatch(openModal(MODAL_TYPES.DELETE_CREATIVES_ERRORS, {
											assigned,
											creatives,
										}));
										return { success: true };
									});
							}

							// Refresh grid after delete to update creative total
							return dispatch(loadCreatives(libraryId));
						}
						return resp;
					});
			},
		}));
	};
}

export function confirmDeleteConcept(onCommitDsp, canDelete, dependentValues){
	let { libraryId, conceptId, conceptName, creativeId } = dependentValues;
	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title				:`Delete Concept - ${conceptName}`,
			className			:"delete-concept-confirm",
			desc				:<DeleteConcept creativeId={creativeId} />,
			isCommitDisabled	:!canDelete,
			commitLabel: 'Delete Concept',
			onCommit: () => {
				return dispatch(onCommitDsp(libraryId, conceptId));
			},
		}
		));
	};
}

function _deleteAsset(dispatch, libraryId, assetType, assets = []){
	let assetTypeLabel = assetType;
	if (isAfterEffectsAsset(assetType)) {
		assetTypeLabel = "After Effects Package";
	}

	return {
		title:     "Delete Asset",
		className: "delete-asset-confirm",
		desc:      <DeleteAsset assetType={assetTypeLabel} assets={assets} />,
		commitLabel:`Delete ${labelWithCount(assetTypeLabel, assets.length)}`,
		onCommit:  () => {
			let groups = {};
			assets.forEach((v) => {
				if(!groups[v.assetType]){
					groups[v.assetType] = [];
				}

				groups[v.assetType].push(v);
			});

			let pp = Object.keys(groups).map((type) => {
				return dispatch(deleteAssets(libraryId, type.toLowerCase(), groups[type].map(asset => asset.id)));
			});

			return Promise.all(pp).then((resp) => {
				// TODO: return something different here for HOPI-1141
				return { success: true };
				// let success = [];
				// resp.forEach(v => {
				// 	if(v.success){
				// 		success.push()
				// 	}
				// });
			});
		},
	};
}


export function confirmDeleteAsset(libraryId, assetType, assets){
	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction(_deleteAsset(dispatch, libraryId, assetType, assets)));
	};
}



function DeleteLibrary({ name, error }){
	if(error){
		return (
			<div className="delete-error">
				<InfoBanner error={error} />
				<p className="text-center">Unable to delete the library {name}.</p>
			</div>
		);
	}

	return (
		<div className="delete-library-confirm">
			<p className="text-center">Are you sure you want to delete the library <span className="delete-library-name">&quot;{name}&quot;</span>?</p>
			<b>
				<p className="text-center">All creatives, and versions will be deleted, asset files will be permanently removed.</p>
				<p className="text-center">This action cannot be undone.</p>
			</b>
		</div>
	);
}
export function confirmDeleteLibrary(onCommitDsp, libraryInfo, onSuccessCallback){
	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title:            `Delete Creative Library: ${libraryInfo.name}`,
			className:        "delete-library",
			commitLabel:      "Delete Library",
			isCommitDisabled: !!(libraryInfo.error),
			desc:             <DeleteLibrary name={libraryInfo.name} error={libraryInfo.error} />,
			onCommit: async () => {
				let response;
				try {
					response = await dispatch(onCommitDsp(libraryInfo.id));
				} catch (e) {
					throw Error(e);
				}

				if (!response.success) {
					dispatch(confirmDeleteLibrary(null, { error: response.payload.errorObj }));
					return response;
				}

				try {
					/*
					* We would like to be able to execute certain callbacks as side-effects on success
					* i.e.: to be able to redirect user to another view.
					* Wrapping side-effect in try-catch to make sure we always return response.
					*/
					if (typeof onSuccessCallback === 'function') {
						await onSuccessCallback();
					}
				} catch (e) {
					// Side-effect error can be just logged and ignored
					console.error(e);
				}

				return response;
			},
		}));
	};
}

function RemoveAssignee({ libraries }){
	return [
		<p key="title">Are you sure you want to clear the assignee of the following libraries</p>
		, (
			<table key="table" className="basic-table confirm-clear-assignee">
				<thead className="header">
					<tr className="headrow">
						<th>Name</th>
						<th>ID</th>
						<th>Assignee</th>
					</tr>
				</thead>
				<tbody>
					{libraries.map((lib) => {
						return (
							<tr key={lib.id}>
								<td>{lib.name}</td>
								<td>{lib.id}</td>
								<td>{lib.assignee}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		)
	];
}

export function confirmRemoveAssignee(deleteAction, confirmProps){
	let { libraries } = confirmProps;
	let titleText = "Clear Assignee";

	return (dispatch) => {
		return dispatch(Confirm.openConfirmAction({
			title: titleText,
			className: "delete-creative-confirm",
			desc: <RemoveAssignee libraries={libraries} />,
			commitLabel: titleText,
			onCommit: () => {
				return dispatch(deleteAction(null, libraries.map(v => v.id)));
			},
		}));
	};
}

export const PRIVATE = {
	DeleteLibrary,
	_deleteAsset,
};

export default createReducer(Confirm.INITIAL_CONFIRM_STATE, Confirm.CONFIRM_HANDLERS);
